import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import MainLayout from '../user-dashboard/MainLayout';
import { useAuth } from '../../contexts/AuthContext';
import { 
  supabase,
  getUserPracticeLog,
  savePracticeLog,
  fetchUserBadges,
  awardBadge,
  fetchSessionStatusFast,
  fetchDashboardDataFast,
  initializeUserSessions
} from '../../supabase';

// Practice data mapped to sessions with dashboard colors
const allPractices = [
  {
    id: 'practice1',
    name: 'Deep Breathing',
    skill: 'Deep Breathing',
    sessionNumber: 1,
    sessionTitle: 'Understanding Chronic Orofacial Pain',
    description:
      "Learn to activate your body's natural relaxation response through controlled breathing techniques.",
    duration: 5,
    videoUrl: '/assets/S1_GuidedPractice_compressed.mp4',
    thumbnail: '/assets/thumbnails/deep-breathing.jpg',
    color: '#1B365D',
  },
  {
    id: 'practice2',
    name: 'Body Scan',
    skill: 'Body Scan',
    sessionNumber: 2,
    sessionTitle: 'Managing Emotional Challenges',
    description:
      'Develop awareness of physical sensations without judgment to reduce pain-related distress.',
    duration: 6,
    videoUrl: '/assets/S2_GuidedPractice_compressed.mp4',
    thumbnail: '/assets/thumbnails/body-scan-icon.jpg',
    color: '#00A0B0',
  },
  {
    id: 'practice3',
    name: 'Catch, Check, and Change',
    skill: 'Catch, Check, and Change',
    sessionNumber: 3,
    sessionTitle: 'Cognitive Strategies',
    description: 'Learn to identify and challenge unhelpful thinking patterns.',
    duration: 2,
    videoUrl: '/assets/S3_GuidedPractice_compressed.mp4',
    thumbnail: '/assets/thumbnails/catch-check-change-icon.jpg',
    color: '#4A90A4',
  },
  {
    id: 'practice4',
    name: 'Goals for Re-engaging in Life',
    skill: 'Goals for Re-engaging in Life',
    sessionNumber: 4,
    sessionTitle: 'Behavioral Activation',
    description:
      'Continue working towards your meaningful life activities and goals despite pain challenges.',
    duration: null, // No video - this is goal tracking
    videoUrl: null,
    thumbnail: '/assets/thumbnails/goals-for-re-engaging-in-life-icon',
    color: '#7FCDCD',
    isGoalBased: true,
  },
  {
    id: 'practice5',
    name: 'Mindfulness of Pain and Discomfort',
    skill: 'Mindfulness of Pain and Discomfort',
    sessionNumber: 5,
    sessionTitle: 'Advanced Mindfulness',
    description:
      'Develop a new relationship with pain through mindful awareness and acceptance.',
    duration: 3,
    videoUrl: '/assets/S5_GuidedPractice_compressed.mp4',
    thumbnail: '/assets/thumbnails/mindful-pain.jpg',
    color: '#B8E6B8',
  },
];

// SVG Components for Video Controls (matched with session pages)
const PlayButtonSVG = ({ size = 32, color = '#374151' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" height={size} viewBox="0 -960 960 960" width={size} fill={color}>
    <path d="m380-300 280-180-280-180v360ZM480-80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320Z"/>
  </svg>
);

const PauseButtonSVG = ({ size = 32, color = '#374151' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" height={size} viewBox="0 -960 960 960" width={size} fill={color}>
    <path d="M360-320h80v-320h-80v320Zm160 0h80v-320h-80v320ZM480-80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320Z"/>
  </svg>
);

const RewindButtonSVG = ({ size = 24, color = '#374151' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" height={size} viewBox="0 -960 960 960" width={size} fill={color}>
    <path d="M440-240 200-480l240-240 56 56-183 184 183 184-56 56Zm264 0L464-480l240-240 56 56-183 184 183 184-56 56Z"/>
  </svg>
);

const ForwardButtonSVG = ({ size = 24, color = '#374151' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" height={size} viewBox="0 -960 960 960" width={size} fill={color}>
    <path d="M383-480 200-664l56-56 240 240-240 240-56-56 183-184Zm264 0L464-664l56-56 240 240-240 240-56-56 183-184Z"/>
  </svg>
);

const ModernPracticeLibrary = () => {
  const { user, userProfile, loading: authLoading } = useAuth();
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [completedPractices, setCompletedPractices] = useState([]);
  const [practiceLog, setPracticeLog] = useState([]);
  const [userBadges, setUserBadges] = useState([]);
  const [completedSessions, setCompletedSessions] = useState([]);
  const [sessionStatuses, setSessionStatuses] = useState({});
  const [selectedPractice, setSelectedPractice] = useState(null);
  const [filteredPractices, setFilteredPractices] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  
  // Video tracking states
  const [watchedDuration, setWatchedDuration] = useState(0);
  const [totalDuration, setTotalDuration] = useState(0);
  const [hasFinishedVideo, setHasFinishedVideo] = useState(false);
  const [showLogPracticeButton, setShowLogPracticeButton] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  
  // Custom video player states
  const [isPlaying, setIsPlaying] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [videoRef, setVideoRef] = useState(null);
  const [showControls, setShowControls] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [volume, setVolume] = useState(1);
  const [lastVolume, setLastVolume] = useState(1);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const videoContainerRef = useRef(null);
  const overlayHideTimeoutRef = useRef(null);
  
  const navigate = useNavigate();
  const location = useLocation();

  // Custom video player functions
  const togglePlayPause = () => {
    if (videoRef) {
      if (isPlaying) {
        videoRef.pause();
        setIsPlaying(false);
      } else {
        videoRef.play();
        setIsPlaying(true);
      }
    }
  };

  const toggleFullscreen = () => {
    const fullscreenTarget = videoContainerRef.current || videoRef;
    if (!fullscreenTarget) return;

    try {
      if (!document.fullscreenElement) {
        if (fullscreenTarget.requestFullscreen) {
          fullscreenTarget.requestFullscreen().then(() => {
            setIsFullscreen(true);
          }).catch(err => {
            console.log('Standard fullscreen failed:', err);
            handleFullscreenFallback();
          });
        } else if (fullscreenTarget.webkitRequestFullscreen) {
          fullscreenTarget.webkitRequestFullscreen();
          setIsFullscreen(true);
        } else if (fullscreenTarget.mozRequestFullScreen) {
          fullscreenTarget.mozRequestFullScreen();
          setIsFullscreen(true);
        } else if (fullscreenTarget.msRequestFullscreen) {
          fullscreenTarget.msRequestFullscreen();
          setIsFullscreen(true);
        } else {
          handleFullscreenFallback();
        }
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen().then(() => {
            setIsFullscreen(false);
          }).catch(() => {
            setIsFullscreen(false);
          });
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
          setIsFullscreen(false);
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen();
          setIsFullscreen(false);
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen();
          setIsFullscreen(false);
        }
      }
    } catch (error) {
      console.log('Fullscreen error:', error);
      handleFullscreenFallback();
    }
  };

  const handleFullscreenFallback = () => {
    if (isMobile) {
      if (videoRef && !isPlaying) {
        videoRef.play();
        setIsPlaying(true);
      }
    }
  };

  const handleVideoClick = () => {
    togglePlayPause();
  };

  const showOverlayControls = () => {
    setShowControls(true);

    if (overlayHideTimeoutRef.current) {
      clearTimeout(overlayHideTimeoutRef.current);
    }

    overlayHideTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, 3000);
  };

  const hideOverlayControls = () => {
    if (overlayHideTimeoutRef.current) {
      clearTimeout(overlayHideTimeoutRef.current);
      overlayHideTimeoutRef.current = null;
    }
    setShowControls(false);
  };

  const formatTime = (seconds) => {
    if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const skipBackward = () => {
    if (!videoRef) return;
    const newTime = Math.max(0, videoRef.currentTime - 10);
    videoRef.currentTime = newTime;
    setWatchedDuration(newTime);
    if (!isPlaying) {
      videoRef.play();
      setIsPlaying(true);
    }
  };

  const skipForward = () => {
    if (!videoRef || !Number.isFinite(totalDuration) || totalDuration <= 0) return;
    const newTime = Math.min(totalDuration, videoRef.currentTime + 10);
    videoRef.currentTime = newTime;
    setWatchedDuration(newTime);
    if (!isPlaying) {
      videoRef.play();
      setIsPlaying(true);
    }
  };

  const handleProgressBarClick = (e) => {
    if (!videoRef || !Number.isFinite(totalDuration) || totalDuration <= 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const progressPercentage = Math.max(0, Math.min(1, clickX / rect.width));
    const newTime = progressPercentage * totalDuration;
    videoRef.currentTime = newTime;
    setWatchedDuration(newTime);
    setVideoProgress(progressPercentage);
  };

  const toggleMute = () => {
    if (volume === 0) {
      setVolume(lastVolume || 1);
      return;
    }
    setLastVolume(volume);
    setVolume(0);
  };

  const handleVideoReady = () => {
    setIsVideoReady(true);
  };

  const handleVideoPause = () => {
    setIsPlaying(false);
  };

  const handleVideoPlay = () => {
    setIsPlaying(true);
  };

  useEffect(() => {
    if (!videoRef) return;
    videoRef.volume = volume;
    videoRef.muted = volume === 0;
  }, [videoRef, volume]);

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Reset video state when practice changes
  useEffect(() => {
    if (selectedPractice) {
      setIsPlaying(false);
      setIsVideoReady(false);
      setVideoProgress(0);
      setWatchedDuration(0);
      setHasFinishedVideo(false);
      setShowLogPracticeButton(false);
      setShowVolumeSlider(false);
    }
  }, [selectedPractice]);

  // Handle window resize for mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    return () => {
      if (overlayHideTimeoutRef.current) {
        clearTimeout(overlayHideTimeoutRef.current);
      }
    };
  }, []);

  // Set userId from auth
  useEffect(() => {
    if (user?.id) {
      setUserId(user.id);
      loadUserData(user.id);
    }
  }, [user]);

  // FIXED: Updated loadUserData function to match dashboard unlock logic
  const loadUserData = async (userId) => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔄 Loading practice library data for user:', userId);

      // Initialize user sessions first to ensure all sessions exist
      await initializeUserSessions(userId);

      // Fetch sessions with detailed progress data directly from user_sessions table
      const { data: sessionsData, error } = await supabase
        .from('user_sessions')
        .select(
          `
          session_number,
          session_completed,
          session_fully_completed,
          session_started,
          video_watched,
          video_completed,
          quiz_completed,
          pain_level,
          pain_level_timestamp,
          goals_set_timestamp,
          weekly_goals,
          next_session_unlock_time,
          quiz_score,
          video_progress_percentage,
          created_at,
          pre_assessment_completed
        `,
        )
        .eq('user_id', userId)
        .order('session_number');

      if (error) {
        console.error('❌ Guided Practice: Error fetching sessions:', error);
        throw new Error(error.message);
      }

      // ADDED: Fetch user's session schedule to check unlock times
      const { data: schedule } = await supabase
        .from('participant_session_schedule')
        .select('*')
        .eq('user_id', userId)
        .single();

      const { data: manualOverrides } = await supabase
        .from('manual_session_overrides')
        .select('*')
        .eq('user_id', userId);

      console.log('📅 User session schedule:', schedule);

      // Session metadata - ALWAYS include session 0
      const sessionMetadata = [
        { 
          id: "session0", 
          number: 0, 
          title: "Welcome & Program Introduction", 
          subtitle: "Get oriented with the program and set expectations.", 
          estimatedTime: "15 min" 
        },
        { 
          id: "session1", 
          number: 1, 
          title: "Taking Charge of Orofacial Pain", 
          subtitle: "Understand chronic pain and practice deep breathing techniques.", 
          estimatedTime: "20 min" 
        },
        { 
          id: "session2", 
          number: 2, 
          title: "Managing Emotional Challenges in Orofacial Pain", 
          subtitle: "Break negative pain spirals with mindfulness and body scan practice.", 
          estimatedTime: "23 min" 
        },
        { 
          id: "session3", 
          number: 3, 
          title: "Working with Negative Thoughts in Orofacial Pain", 
          subtitle: "Challenge negative thoughts using catch, check, and change techniques.", 
          estimatedTime: "19 min" 
        },
        { 
          id: "session4", 
          number: 4, 
          title: "Not Letting Orofacial Pain Get the Best of Us", 
          subtitle: "Practice acceptance and re-engage in meaningful life activities.", 
          estimatedTime: "22 min" 
        },
        { 
          id: "session5", 
          number: 5, 
          title: "Facing Forward: Rolling with Orofacial Pain & Staying Resilient", 
          subtitle: "Maintain progress and build long-term resilience strategies.", 
          estimatedTime: "22 min" 
        }
      ];
      
      // FIXED: Transform sessions with REAL backend progress data AND scheduled unlock checking (matching dashboard)
      const transformedSessions = sessionMetadata.map((metadata) => {
        const sessionData = sessionsData?.find(
          (s) => s.session_number === metadata.number,
        );
        const override = manualOverrides?.find(
          (o) => o.session_number === metadata.number,
        );

        // FIXED: Determine if session is unlocked - check schedule first (matching dashboard logic)
        let isUnlocked = metadata.number === 0; // Session 0 is always unlocked

        if (override) {
          const overrideStatus = (override.override_status || '').toLowerCase();
          isUnlocked = ['unlocked', 'complete', 'completed'].includes(
            overrideStatus,
          );
        } else if (metadata.number === 1) {
          // Session 1 unlocks after Session 0 completion
          const sessionZero = sessionsData?.find((s) => s.session_number === 0);
          if (sessionZero) {
            isUnlocked =
              sessionZero.session_completed ||
              (sessionZero.video_completed && sessionZero.quiz_completed);
            console.log(
              `🔍 Guided Practice Session 1 unlock check - Session 0 completed: ${sessionZero.session_completed}, video completed: ${sessionZero.video_completed}, quiz completed: ${sessionZero.quiz_completed}, unlocked: ${isUnlocked}`,
            );
          }
        } else if (metadata.number > 1) {
          // Sessions 2-5 unlock when scheduled unlock time has passed.
          if (schedule) {
            const scheduledUnlockTime =
              schedule[`session_${metadata.number}_unlock_time`];
            if (scheduledUnlockTime) {
              const now = new Date();
              const unlockTime = new Date(scheduledUnlockTime);
              const isScheduledUnlocked = now >= unlockTime;

              console.log(
                `📅 Guided Practice Session ${metadata.number} scheduled check:`,
                {
                  scheduledTime: unlockTime,
                  currentTime: now,
                  isScheduledUnlocked,
                },
              );

              if (!isScheduledUnlocked) {
                isUnlocked = false; // Still time-locked
                // Return early with locked status
                const progress = {
                  preAssessment: sessionData?.pre_assessment_completed || false,
                  goalSetting:
                    sessionData?.weekly_goals !== null &&
                    sessionData?.weekly_goals !== undefined &&
                    sessionData?.weekly_goals !== '',
                  video:
                    sessionData?.video_watched ||
                    sessionData?.video_completed ||
                    false,
                  quiz: sessionData?.quiz_completed || false,
                  // postAssessment: sessionData?.post_assessment_completed || false
                };

                return {
                  ...metadata,
                  completed:
                    sessionData?.session_completed ||
                    sessionData?.session_fully_completed ||
                    false,
                  unlocked: false,
                  progress: progress,
                  sessionStarted: sessionData?.session_started || false,
                  painLevel: sessionData?.pain_level,
                  quizScore: sessionData?.quiz_score,
                  videoProgress: sessionData?.video_progress_percentage || 0,
                  nextSessionUnlockTime: sessionData?.next_session_unlock_time,
                  scheduledUnlockTime: unlockTime,
                };
              }
              isUnlocked = true;
            } else {
              isUnlocked = false;
            }
          } else {
            isUnlocked = false;
          }
        }

        // FIXED: Updated progress tracking with correct post-assessment field (matching dashboard)
        const progress = {
          // Pre-Assessment: completed if pre-assessment has been completed
          preAssessment: sessionData?.pre_assessment_completed || false,

          // Goal Setting: completed if weekly goals have been set (for sessions 2+)
          goalSetting:
            sessionData?.weekly_goals !== null &&
            sessionData?.weekly_goals !== undefined &&
            sessionData?.weekly_goals !== '',

          // Video: completed if video has been watched/completed
          video:
            sessionData?.video_watched || sessionData?.video_completed || false,

          // Quiz: completed if quiz has been completed
          quiz: sessionData?.quiz_completed || false,

          // Post-Assessment: completed if post-assessment has been completed
          // postAssessment: sessionData?.post_assessment_completed || false
        };

        return {
          ...metadata,
          completed:
            sessionData?.session_completed ||
            sessionData?.session_fully_completed ||
            false,
          unlocked: isUnlocked,
          progress: progress,
          // Additional data for potential future use
          sessionStarted: sessionData?.session_started || false,
          painLevel: sessionData?.pain_level,
          quizScore: sessionData?.quiz_score,
          videoProgress: sessionData?.video_progress_percentage || 0,
          nextSessionUnlockTime: sessionData?.next_session_unlock_time,
        };
      });

      console.log(
        '✅ Guided Practice: Sessions loaded with updated unlock logic:',
        transformedSessions,
      );
      setSessions(transformedSessions);

      // Load user practice log
      const practiceLogData = await getUserPracticeLog(userId);
      setPracticeLog(practiceLogData || []);
      console.log(
        '✅ Practice log loaded:',
        practiceLogData?.length || 0,
        'entries',
      );

      // Load user badges
      const badgesData = await fetchUserBadges(userId);
      setUserBadges(badgesData || []);
      console.log('✅ User badges loaded:', badgesData?.length || 0, 'badges');

      // Update available sessions list: include only unlocked sessions with completed video.
      const completedSessionsList = [];
      const statusMap = {};

      transformedSessions.forEach((session) => {
        statusMap[session.number] = {
          isSessionCompleted: session.completed,
          videoWatched: session.progress?.video || false,
          isUnlocked: session.unlocked,
        };

        // Session practices require both unlocked access and video completion.
        if (session.unlocked && session.progress?.video) {
          completedSessionsList.push(session.number);
        }
      });

      setCompletedSessions(completedSessionsList);
      setSessionStatuses(statusMap);
      console.log('✅ Available sessions (unlocked):', completedSessionsList);

      // Process practice completion data from practice log
      const practiceCompletionMap = {};
      (practiceLogData || []).forEach(log => {
        const key = log.skill;
        if (!practiceCompletionMap[key]) {
          const matchingPractice = allPractices.find(p => p.skill === key);
          practiceCompletionMap[key] = {
            practiceId: matchingPractice?.id,
            skill: key,
            completionCount: 0,
            lastWatchPercentage: 0,
            totalTimeWatched: 0
          };
        }
        practiceCompletionMap[key].completionCount++;
        practiceCompletionMap[key].totalTimeWatched += (log.minutes || 0);
        practiceCompletionMap[key].lastWatchPercentage = 100;
      });

      setCompletedPractices(Object.values(practiceCompletionMap));
      console.log('✅ Practice completion data processed:', Object.keys(practiceCompletionMap).length, 'skills');

    } catch (error) {
      console.error('❌ Error loading user data:', error);
      setError('Failed to load practice data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Filter practices based on completed video sessions
  const getAvailablePractices = () => {
    return allPractices.filter(practice => {
      return completedSessions.includes(practice.sessionNumber);
    });
  };

  // Check if a practice is completed
  const isPracticeCompleted = (practiceId) => {
    return completedPractices.some(p => p.practiceId === practiceId);
  };

  // Get practice progress data
  const getPracticeProgress = (practiceId) => {
    return completedPractices.find(p => p.practiceId === practiceId) || null;
  };

  // Video event handlers
  const handleVideoProgress = (progress) => {
    setVideoProgress(progress.played);
    setWatchedDuration(progress.playedSeconds);
  };

  const handleVideoEnd = () => {
    setHasFinishedVideo(true);
    setShowLogPracticeButton(true);
    setIsPlaying(false);
  };

  const handleVideoDuration = (duration) => {
    setTotalDuration(duration);
  };

  // Auto-log practice function
  const handleAutoLogPractice = async () => {
    if (!selectedPractice || !userId) return;
    
    const practiceData = {
      skill: selectedPractice.skill,
      minutes: selectedPractice.isGoalBased ? 10 : Math.ceil(selectedPractice.duration), // Default 10 minutes for goal-based
      practiceType: selectedPractice.isGoalBased ? 'goal-work' : 'video-guided',
      date: new Date().toISOString().split('T')[0],
      sessionNumber: selectedPractice.sessionNumber
    };

    try {
      console.log('📝 Logging practice:', practiceData);
      
      const success = await savePracticeLog(userId, practiceData);
      
      if (success) {
        console.log('✅ Practice logged successfully');
        
        const newLogEntry = {
          ...practiceData,
          id: Date.now(),
          timestamp: Date.now(),
          created_at: new Date().toISOString()
        };
        setPracticeLog(prev => [...prev, newLogEntry]);

        setCompletedPractices(prev => {
          const existing = prev.find(p => p.skill === selectedPractice.skill);
          if (existing) {
            return prev.map(p => 
              p.skill === selectedPractice.skill 
                ? {
                    ...p,
                    completionCount: p.completionCount + 1,
                    totalTimeWatched: p.totalTimeWatched + (selectedPractice.duration || 10),
                    lastWatchPercentage: 100
                  }
                : p
            );
          } else {
            return [...prev, {
              practiceId: selectedPractice.id,
              skill: selectedPractice.skill,
              completionCount: 1,
              lastWatchPercentage: 100,
              totalTimeWatched: selectedPractice.duration || 10
            }];
          }
        });

        await checkAndAwardBadges(userId, practiceData, newLogEntry);
        
        setShowLogPracticeButton(false);
        setVideoProgress(0);
        setWatchedDuration(0);
        setHasFinishedVideo(false);
        
      } else {
        throw new Error('Failed to save practice log');
      }
      
    } catch (error) {
      console.error('❌ Error logging practice:', error);
      setError('Failed to log practice. Please try again.');
    }
  };

  // Badge checking system
  const checkAndAwardBadges = async (userId, practiceData, newLogEntry) => {
    try {
      console.log('🏆 Checking for badge eligibility...');
      
      const updatedPracticeLog = [...practiceLog, newLogEntry];
      const skillCounts = {};
      updatedPracticeLog.forEach(log => {
        skillCounts[log.skill] = (skillCounts[log.skill] || 0) + 1;
      });

      const badgeCriteria = [
        {
          name: 'First Practice',
          condition: () => practiceLog.length === 0,
          description: 'Completed your first practice session'
        },
        {
          name: 'Deep Breathing Master',
          condition: () => skillCounts['Deep Breathing'] >= 5,
          description: 'Completed 5 Deep Breathing sessions'
        },
        {
          name: 'Weekly Warrior',
          condition: () => {
            const thisWeek = updatedPracticeLog.filter(log => {
              const logDate = new Date(log.date);
              const weekAgo = new Date();
              weekAgo.setDate(weekAgo.getDate() - 7);
              return logDate >= weekAgo;
            }).length;
            return thisWeek >= 7;
          },
          description: 'Practiced 7 times in one week'
        }
      ];

      for (const badge of badgeCriteria) {
        if (!userBadges.includes(badge.name) && badge.condition()) {
          console.log('🏆 Awarding badge:', badge.name);
          const success = await awardBadge(userId, badge.name);
          if (success) {
            setUserBadges(prev => [...prev, badge.name]);
            setTimeout(() => {
              alert(`🏆 Badge Earned: ${badge.name}!\n${badge.description}`);
            }, 500);
          }
        }
      }

    } catch (error) {
      console.error('❌ Error checking badges:', error);
    }
  };

  // Handle practice click - select practice for right panel
  const handlePracticeClick = (practice) => {
    setSelectedPractice(practice);
    setVideoProgress(0);
    setWatchedDuration(0);
    setHasFinishedVideo(false);
    setShowLogPracticeButton(false);
    // Reset video player state
    setIsPlaying(false);
    setIsVideoReady(false);

    // For goal-based practice, automatically show the log button
    if (practice.isGoalBased) {
      setShowLogPracticeButton(true);
    }
  };

  // FIXED: getAvailableSkills function - only show skills from unlocked sessions
  const getAvailableSkills = useCallback(() => {
    console.log('🔍 getAvailableSkills called with sessions:', sessions.map(s => ({ number: s.number, unlocked: s.unlocked, title: s.title })));
    
    const skillsBySession = {
      1: ['Deep Breathing'],
      2: ['Body Scan'],
      3: ['Catch, Check, and Change'],
      4: ['Acceptance', 'Goals for Re-engaging in Life'],
      5: ['Mindfulness of Pain and Discomfort'],
    };

    const availableSkills = [];
    
    // Add skills from all UNLOCKED sessions only
    sessions.forEach(session => {
      console.log(`🔍 Checking session ${session.number}: unlocked=${session.unlocked}, number>${session.number > 0}`);
      
      if (session.unlocked && session.number > 0) { // Skip session 0 (intro) and only include unlocked sessions
        const sessionSkills = skillsBySession[session.number] || [];
        availableSkills.push(...sessionSkills);
        console.log(`✅ Adding skills from unlocked session ${session.number}:`, sessionSkills);
      } else {
        console.log(`❌ Skipping session ${session.number} - unlocked: ${session.unlocked}, number: ${session.number}`);
      }
    });

    // Remove duplicates and return
    const uniqueSkills = [...new Set(availableSkills)];
    console.log('🎯 Final available skills for user:', uniqueSkills);
    return uniqueSkills;
  }, [sessions]);

  // Filter and sort practices
  useEffect(() => {
    const filtered = getAvailablePractices().sort((a, b) => a.sessionNumber - b.sessionNumber);
    setFilteredPractices(filtered);
  }, [completedSessions]);

  // Error retry function
  const retryLoadData = () => {
    if (userId) {
      setError(null);
      loadUserData(userId);
    }
  };

  if (loading || authLoading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        backgroundColor: '#F8F9FA',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <MainLayout currentSessions={sessions} currentPath={location.pathname}>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh'
          }}>
            <div style={{
              textAlign: 'center'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                border: '3px solid #f1f3f4',
                borderTop: '3px solid #1B365D',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 20px'
              }} />
              <p style={{ 
                color: '#374151', 
                fontFamily: 'system-ui, -apple-system, sans-serif',
                fontSize: '16px',
                fontWeight: '400'
              }}>
                Loading Practice Library...
              </p>
            </div>
          </div>
        </MainLayout>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        backgroundColor: '#F8F9FA',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
       <MainLayout currentSessions={sessions} currentPath={location.pathname}>
          <div style={{
            maxWidth: '1400px',
            margin: '0 auto',
            padding: '32px 40px'
          }}>
            <div style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              padding: '48px',
              textAlign: 'center',
              border: '1px solid #E5E7EB',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
            }}>
              <h2 style={{ color: '#dc3545', marginBottom: '20px' }}>Error Loading Practice Library</h2>
              <p style={{ marginBottom: '20px' }}>{error}</p>
              <button 
                onClick={retryLoadData}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#1B365D',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '20px',
                  cursor: 'pointer',
                  fontFamily: 'system-ui, -apple-system, sans-serif'
                }}
              >
                Try Again
              </button>
            </div>
          </div>
        </MainLayout>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: '#F8F9FA',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
     <MainLayout currentSessions={sessions} currentPath={location.pathname}>


        <div style={{
          flex: 1,
          width: '100%',
          padding: isMobile ? '16px 20px' : '32px 40px',
          overflow: 'auto',
          paddingBottom: isMobile ? '80px' : '32px',
          ...(isMobile && {
            width: '100%',
            maxWidth: '100vw',
            overflowX: 'hidden',
            boxSizing: 'border-box'
          })
        }}>
          {/* Back Button - Desktop Only */}
          {!isMobile && (
            <div style={{
              paddingTop: '32px',
              marginBottom: '16px'
            }}>
              <button
                onClick={() => navigate('/dashboard')}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 0',
                  color: '#374151',
                  fontSize: '14px',
                  fontWeight: '500',
                  fontFamily: 'system-ui, -apple-system, sans-serif'
                }}
                onMouseEnter={(e) => {
                  e.target.style.opacity = '0.7';
                }}
                onMouseLeave={(e) => {
                  e.target.style.opacity = '1';
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px" fill="#374151">
                  <path d="m313-440 224 224-57 56-320-320 320-320 57 56-224 224h487v80H313Z"/>
                </svg>
                Back to Dashboard
              </button>
            </div>
          )}

          {/* Header */}
          <div style={{
            padding: isMobile ? '0' : '0',
            backgroundColor: '#F8F9FA',
            marginBottom: '32px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: isMobile ? 'flex-start' : 'center',
              marginBottom: isMobile ? '0' : '16px',
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? '0' : '8px',
              paddingTop: isMobile ? '24px' : '0'
            }}>
              {isMobile ? (
                // Mobile Layout - Logo and title side-by-side
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  width: '100%'
                }}>
                  {/* Logo Section */}
                  <div style={{
                    width: 'calc(40% - 8px)',
                    height: '82px',
                    backgroundColor: '#FFFFFF',
                    borderRadius: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #E5E7EB',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                    flexShrink: 0
                  }}>
                    <img 
                      src="/assets/FFWLOGO.png"
                      alt="Face Forward Web Logo"
                      style={{
                        width: '80%',
                        filter: 'brightness(0) saturate(100%) invert(11%) sepia(66%) saturate(1347%) hue-rotate(202deg) brightness(93%) contrast(107%)',
                        height: '60%',
                        objectFit: 'contain'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    {/* SVG fallback */}
                    <svg width="40" height="25" fill="#1B365D" viewBox="0 0 24 24" style={{ display: 'none' }}>
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                  </div>

                  {/* Title - Takes remaining width */}
                  <div style={{
                    backgroundColor: '#FFFFFF',
                    borderRadius: '16px',
                    padding: '20px 24px',
                    border: '1px solid #E5E7EB',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 1,
                    height: '82px'
                  }}>
                    <h1 style={{
                      fontSize: '24px',
                      fontWeight: '600',
                      color: '#1B365D',
                      margin: 0,
                      letterSpacing: '-1px',
                      fontFamily: 'system-ui, -apple-system, sans-serif',
                      textAlign: 'center'
                    }}>
                      Guided Practices
                    </h1>
                  </div>
                </div>
              ) : (
                // Desktop Layout
                <h1 style={{
                  fontSize: '48px',
                  fontWeight: '600',
                  color: '#1B365D',
                  margin: 0,
                  letterSpacing: '-1px',
                  fontFamily: 'system-ui, -apple-system, sans-serif'
                }}>
                  Guided Practices
                </h1>
              )}
            </div>
            {/* Desktop subtitle */}
            {!isMobile && (
              <p style={{
                fontSize: '16px',
                fontWeight: '400',
                color: '#6B7280',
                margin: '0',
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }}>
                Watch guided practice videos for each technique
              </p>
            )}
          </div>

          {/* Split Screen Layout */}
          {filteredPractices.length > 0 ? (
            <div style={{
              display: isMobile ? 'flex' : 'grid',
              flexDirection: isMobile ? 'column' : undefined,
              gridTemplateColumns: isMobile ? undefined : '400px 1fr',
              gap: isMobile ? '20px' : '20px',
              flex: 1,
              overflow: isMobile ? 'visible' : 'hidden'
            }}>
              {/* Left Side - Practice List */}
              <div style={{
                backgroundColor: '#FFFFFF',
                borderRadius: '16px',
                border: '1px solid #E5E7EB',
                padding: isMobile ? '16px' : '24px',
                overflow: 'auto',
                height: isMobile ? 'auto' : '100%',
                order: isMobile ? 2 : 1,
                maxHeight: isMobile ? '400px' : undefined,
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
              }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#374151',
                  margin: '0 0 20px 0',
                  fontFamily: 'system-ui, -apple-system, sans-serif'
                }}>
                  Available Practices
                </h3>
                
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }}>
                  {filteredPractices.map(practice => {
                    const progress = getPracticeProgress(practice.id);
                    const isSelected = selectedPractice?.id === practice.id;
                    
                    return (
                      <div
                        key={practice.id}
                        style={{
                          backgroundColor: isSelected ? '#1B365D' : '#F8F9FA',
                          borderRadius: '12px',
                          border: '1px solid #E5E7EB',
                          padding: '16px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onClick={() => handlePracticeClick(practice)}
                        onMouseEnter={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.backgroundColor = '#EFF6FF';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.backgroundColor = '#F8F9FA';
                          }
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px'
                        }}>
                          {/* Practice Icon */}
                          <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '8px',
                            backgroundColor: practice.color,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}>
                            <img 
                              src={`/assets/${practice.name.toLowerCase().replace(/[,\s]+/g, '-')}.png`}
                              alt={practice.name}
                              style={{
                                width: '24px',
                                height: '24px',
                                objectFit: 'contain',
                                filter: 'brightness(0) invert(1)'
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                // Try the icon version
                                const iconImg = document.createElement('img');
                                iconImg.src = `/assets/${practice.name.toLowerCase().replace(/[,\s&]+/g, '-')}-icon.png`;
                                iconImg.style.width = '24px';
                                iconImg.style.height = '24px';
                                iconImg.style.objectFit = 'contain';
                                iconImg.style.filter = 'brightness(0) invert(1)';
                                iconImg.onError = () => {
                                  iconImg.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                };
                                e.target.parentNode.insertBefore(iconImg, e.target.nextSibling);
                              }}
                            />
                            <div style={{
                              width: '24px',
                              height: '24px',
                              display: 'none',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '12px',
                              fontWeight: '600',
                              color: '#FFFFFF'
                            }}>
                              S{practice.sessionNumber}
                            </div>
                          </div>
                          
                          {/* Practice Info */}
                          <div style={{ flex: 1 }}>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              marginBottom: '4px'
                            }}>
                              <h4 style={{
                                fontSize: '14px',
                                fontWeight: '600',
                                color: isSelected ? '#FFFFFF' : '#374151',
                                margin: 0,
                                lineHeight: '1.3'
                              }}>
                                {practice.name}
                              </h4>
                              <div style={{
                                backgroundColor: isSelected ? 'rgba(255, 255, 255, 0.2)' : '#EFF6FF',
                                color: isSelected ? '#FFFFFF' : '#1B365D',
                                borderRadius: '12px',
                                padding: '2px 8px',
                                fontSize: '10px',
                                fontWeight: '600',
                                border: '1px solid #E5E7EB'
                              }}>
                                S{practice.sessionNumber}
                              </div>
                            </div>
                            
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <span style={{
                                fontSize: '12px',
                                color: isSelected ? 'rgba(255, 255, 255, 0.7)' : '#6B7280'
                              }}>
                                {practice.isGoalBased ? 'Goal tracking' : `${practice.duration} minutes`}
                              </span>
                              
                              {progress && progress.completionCount > 0 && (
                                <span style={{
                                  fontSize: '11px',
                                  color: '#374151',
                                  backgroundColor: '#FFFFFF',
                                  padding: '2px 6px',
                                  borderRadius: '8px',
                                  border: '1px solid #E5E7EB',
                                  fontWeight: '500'
                                }}>
                                  {progress.completionCount}x completed
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Right Side - Content Area */}
              <div style={{
                backgroundColor: '#FFFFFF',
                borderRadius: '16px',
                border: '1px solid #E5E7EB',
                padding: isMobile ? '20px' : '32px',
                display: 'flex',
                flexDirection: 'column',
                minHeight: isMobile ? 'auto' : 'calc(100vh - 280px)',
                overflow: 'auto',
                order: isMobile ? 1 : 2,
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
              }}>
                {selectedPractice ? (
                  <>
                    {/* Header */}
                    <div style={{
                      marginBottom: '24px'
                    }}>
                      <h2 style={{
                        fontSize: isMobile ? '22px' : '28px',
                        fontWeight: '600',
                        color: '#374151',
                        margin: '0 0 8px 0',
                        fontFamily: 'system-ui, -apple-system, sans-serif'
                      }}>
                        {selectedPractice.name}
                      </h2>
                      <p style={{
                        fontSize: isMobile ? '14px' : '16px',
                        color: '#6B7280',
                        margin: '0 0 16px 0',
                        lineHeight: '1.5'
                      }}>
                        {selectedPractice.description}
                      </p>
                      
                      <div style={{
                        display: 'flex',
                        gap: '12px',
                        alignItems: 'center',
                        flexWrap: 'wrap'
                      }}>
                        {!selectedPractice.isGoalBased && (
                          <div style={{
                            backgroundColor: '#EFF6FF',
                            color: '#1B365D',
                            padding: '6px 12px',
                            borderRadius: '16px',
                            fontSize: isMobile ? '12px' : '14px',
                            fontWeight: '500',
                            border: '1px solid #3B82F6'
                          }}>
                            {selectedPractice.duration} minutes
                          </div>
                        )}
                        <div style={{
                          backgroundColor: '#F8F9FA',
                          color: '#374151',
                          padding: '6px 12px',
                          borderRadius: '16px',
                          fontSize: isMobile ? '12px' : '14px',
                          fontWeight: '500',
                          border: '1px solid #E5E7EB'
                        }}>
                          Session {selectedPractice.sessionNumber}
                        </div>
                      </div>
                    </div>

                    {selectedPractice.isGoalBased ? (
                      /* Goal-based content */
                      <div style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        backgroundColor: '#F8F9FA',
                        borderRadius: '12px',
                        padding: isMobile ? '32px 20px' : '48px 32px',
                        marginBottom: '24px',
                        border: '1px solid #E5E7EB'
                      }}>
                        <div style={{
                          width: '80px',
                          height: '80px',
                          borderRadius: '50%',
                          backgroundColor: selectedPractice.color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginBottom: '24px'
                        }}>
                          <svg width="40" height="40" fill="#FFFFFF" viewBox="0 0 24 24">
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                          </svg>
                        </div>
                        <h3 style={{
                          fontSize: isMobile ? '18px' : '22px',
                          fontWeight: '600',
                          color: '#374151',
                          margin: '0 0 12px 0',
                          fontFamily: 'system-ui, -apple-system, sans-serif'
                        }}>
                          Continue Your Goals
                        </h3>
                        <p style={{
                          fontSize: isMobile ? '14px' : '16px',
                          color: '#6B7280',
                          margin: '0 0 24px 0',
                          lineHeight: '1.5',
                          maxWidth: '400px'
                        }}>
                          Don't forget to work on your meaningful life activities and goals. Every small step counts toward re-engaging in what matters to you.
                        </p>
                        <div style={{
                          backgroundColor: '#EFF6FF',
                          border: '1px solid #3B82F6',
                          borderRadius: '12px',
                          padding: '16px',
                          marginBottom: '20px',
                          maxWidth: '500px'
                        }}>
                          <h4 style={{
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#374151',
                            margin: '0 0 8px 0'
                          }}>
                            Remember to:
                          </h4>
                          <ul style={{
                            fontSize: '13px',
                            color: '#6B7280',
                            margin: 0,
                            paddingLeft: '20px',
                            lineHeight: '1.4'
                          }}>
                            <li>Review the goals you set in Session 4</li>
                            <li>Take one small step toward a meaningful activity</li>
                            <li>Practice acceptance while staying engaged</li>
                            <li>Celebrate progress, no matter how small</li>
                          </ul>
                        </div>
                      </div>
                    ) : (
                      /* Video Player */
                      <div 
                        ref={videoContainerRef}
                        style={{ 
                          backgroundColor: 'transparent', 
                          borderRadius: '12px', 
                          marginBottom: '24px',
                          position: 'relative',
                          overflow: 'hidden',
                          border: '1px solid #E5E7EB',
                          width: '100%',
                          maxWidth: '100%',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={showOverlayControls}
                        onMouseMove={showOverlayControls}
                        onMouseLeave={hideOverlayControls}
                        onClick={() => {
                          handleVideoClick();
                          showOverlayControls();
                        }}
                      >
                        {selectedPractice.videoUrl ? (
                          <>
                            <video
                              ref={setVideoRef}
                              src={selectedPractice.videoUrl}
                              style={{
                                width: '100%',
                                height: 'auto',
                                display: 'block'
                              }}
                              onTimeUpdate={(e) => {
                                const progress = e.target.duration > 0 ? e.target.currentTime / e.target.duration : 0;
                                handleVideoProgress({ played: progress, playedSeconds: e.target.currentTime });
                              }}
                              onEnded={handleVideoEnd}
                              onLoadedMetadata={(e) => {
                                handleVideoDuration(e.target.duration);
                                handleVideoReady();
                              }}
                              onPause={handleVideoPause}
                              onPlay={handleVideoPlay}
                              controls={false}
                              onContextMenu={(e) => e.preventDefault()}
                            />
                            
                            {/* Custom Controls Overlay */}
                            <div style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: showControls ? 'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, transparent 30%, transparent 70%, rgba(0,0,0,0.3) 100%)' : 'transparent',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'background 0.3s ease',
                              pointerEvents: showControls ? 'auto' : 'none'
                            }}>
                              
                              {/* Top Controls */}
                              <div style={{
                                position: 'absolute',
                                top: '16px',
                                right: '16px',
                                display: 'flex',
                                gap: '8px',
                                opacity: showControls ? 1 : 0,
                                transition: 'opacity 0.3s ease'
                              }}>
                                {/* Fullscreen Button */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleFullscreen();
                                  }}
                                  style={{
                                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '8px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <svg width="20" height="20" fill="white" viewBox="0 0 24 24">
                                    {isFullscreen ? (
                                      <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/>
                                    ) : (
                                      <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
                                    )}
                                  </svg>
                                </button>
                              </div>

                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: isMobile ? '12px' : '20px',
                                  opacity: showControls ? 1 : 0,
                                  transition: 'opacity 0.3s ease, transform 0.3s ease',
                                  transform: showControls ? 'scale(1)' : 'scale(0.8)'
                                }}
                              >
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    skipBackward();
                                  }}
                                  disabled={!isVideoReady}
                                  style={{
                                    backgroundColor: '#FFFFFF',
                                    border: '1px solid #E5E7EB',
                                    borderRadius: '50%',
                                    width: isMobile ? '52px' : '64px',
                                    height: isMobile ? '52px' : '64px',
                                    cursor: isVideoReady ? 'pointer' : 'not-allowed',
                                    opacity: isVideoReady ? 1 : 0.6,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                                  }}
                                  aria-label="Skip backward 10 seconds"
                                >
                                  <RewindButtonSVG size={isMobile ? 20 : 24} color="#374151" />
                                </button>

                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    togglePlayPause();
                                  }}
                                  style={{
                                    backgroundColor: '#FFFFFF',
                                    border: '1px solid #E5E7EB',
                                    borderRadius: '50%',
                                    width: isMobile ? '64px' : '80px',
                                    height: isMobile ? '64px' : '80px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                                  }}
                                  aria-label={isPlaying ? 'Pause video' : 'Play video'}
                                >
                                  {isPlaying ? (
                                    <PauseButtonSVG size={isMobile ? 24 : 32} color="#374151" />
                                  ) : (
                                    <PlayButtonSVG size={isMobile ? 24 : 32} color="#374151" />
                                  )}
                                </button>

                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    skipForward();
                                  }}
                                  disabled={!isVideoReady}
                                  style={{
                                    backgroundColor: '#FFFFFF',
                                    border: '1px solid #E5E7EB',
                                    borderRadius: '50%',
                                    width: isMobile ? '52px' : '64px',
                                    height: isMobile ? '52px' : '64px',
                                    cursor: isVideoReady ? 'pointer' : 'not-allowed',
                                    opacity: isVideoReady ? 1 : 0.6,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                                  }}
                                  aria-label="Skip forward 10 seconds"
                                >
                                  <ForwardButtonSVG size={isMobile ? 20 : 24} color="#374151" />
                                </button>
                              </div>

                              {/* Bottom Progress Bar */}
                              <div style={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                height: '4px',
                                backgroundColor: 'rgba(255, 255, 255, 0.3)'
                              }}>
                                <div style={{
                                  width: `${videoProgress * 100}%`,
                                  height: '100%',
                                  backgroundColor: '#1B365D',
                                  transition: 'width 0.1s ease'
                                }} />
                              </div>
                            </div>
                          </>
                        ) : (
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            height: '100%',
                            color: 'white'
                          }}>
                            <div style={{ textAlign: 'center' }}>
                              <svg width={isMobile ? "48" : "64"} height={isMobile ? "48" : "64"} fill="white" viewBox="0 0 24 24" style={{ marginBottom: '16px' }}>
                                <path d="M8 5v14l11-7z"/>
                              </svg>
                              <p style={{ fontSize: isMobile ? '16px' : '18px' }}>Practice video will be available here</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {!selectedPractice.isGoalBased && selectedPractice.videoUrl && (
                      <div style={{
                        backgroundColor: '#FFFFFF',
                        borderRadius: '12px',
                        border: '1px solid #E5E7EB',
                        padding: isMobile ? '12px' : '16px',
                        marginBottom: '20px'
                      }}>
                        <div
                          style={{
                            height: '8px',
                            backgroundColor: '#F3F4F6',
                            borderRadius: '4px',
                            position: 'relative',
                            overflow: 'hidden',
                            cursor: 'pointer',
                            marginBottom: '14px',
                            border: '1px solid #E5E7EB'
                          }}
                          onClick={handleProgressBarClick}
                        >
                          <div style={{
                            height: '100%',
                            width: `${videoProgress * 100}%`,
                            backgroundColor: '#1B365D',
                            borderRadius: '4px',
                            transition: 'width 0.1s ease'
                          }} />
                        </div>

                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '12px',
                          flexWrap: isMobile ? 'wrap' : 'nowrap'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px'
                          }}>
                            <button
                              onClick={togglePlayPause}
                              disabled={!isVideoReady}
                              style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                backgroundColor: '#1B365D',
                                border: '1px solid #E5E7EB',
                                color: '#FFFFFF',
                                cursor: isVideoReady ? 'pointer' : 'not-allowed',
                                opacity: isVideoReady ? 1 : 0.6,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                              aria-label={isPlaying ? 'Pause video' : 'Play video'}
                            >
                              {isPlaying ? (
                                <PauseButtonSVG size={16} color="#FFFFFF" />
                              ) : (
                                <PlayButtonSVG size={16} color="#FFFFFF" />
                              )}
                            </button>

                            <button
                              onClick={skipBackward}
                              disabled={!isVideoReady}
                              style={{
                                backgroundColor: '#FFFFFF',
                                border: '1px solid #E5E7EB',
                                borderRadius: '20px',
                                color: '#374151',
                                cursor: isVideoReady ? 'pointer' : 'not-allowed',
                                opacity: isVideoReady ? 1 : 0.6,
                                padding: '6px 12px',
                                fontSize: '12px',
                                fontWeight: '500',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                            >
                              <RewindButtonSVG size={12} color="#374151" />
                              10s
                            </button>

                            <button
                              onClick={skipForward}
                              disabled={!isVideoReady}
                              style={{
                                backgroundColor: '#FFFFFF',
                                border: '1px solid #E5E7EB',
                                borderRadius: '20px',
                                color: '#374151',
                                cursor: isVideoReady ? 'pointer' : 'not-allowed',
                                opacity: isVideoReady ? 1 : 0.6,
                                padding: '6px 12px',
                                fontSize: '12px',
                                fontWeight: '500',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                            >
                              10s
                              <ForwardButtonSVG size={12} color="#374151" />
                            </button>
                          </div>

                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            marginLeft: isMobile ? 'auto' : 0
                          }}>
                            <span style={{
                              fontSize: '13px',
                              color: '#374151',
                              fontFamily: 'monospace'
                            }}>
                              {formatTime(watchedDuration)} / {formatTime(totalDuration)}
                            </span>

                            <div
                              style={{ position: 'relative' }}
                              onMouseEnter={() => !isMobile && setShowVolumeSlider(true)}
                              onMouseLeave={() => !isMobile && setShowVolumeSlider(false)}
                            >
                              <button
                                onClick={() => {
                                  if (isMobile) {
                                    setShowVolumeSlider(!showVolumeSlider);
                                  } else {
                                    toggleMute();
                                  }
                                }}
                                style={{
                                  backgroundColor: '#FFFFFF',
                                  border: '1px solid #E5E7EB',
                                  borderRadius: '8px',
                                  color: '#374151',
                                  cursor: 'pointer',
                                  padding: '8px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                                title={`Volume: ${Math.round(volume * 100)}%`}
                                aria-label="Volume"
                              >
                                <svg width="16" height="16" fill="#374151" viewBox="0 0 24 24">
                                  {volume === 0 ? (
                                    <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                                  ) : volume < 0.5 ? (
                                    <path d="M7 9v6h4l5 5V4l-5 5H7z"/>
                                  ) : (
                                    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/>
                                  )}
                                </svg>
                              </button>

                              {showVolumeSlider && (
                                <div style={{
                                  position: 'absolute',
                                  bottom: '100%',
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  marginBottom: '8px',
                                  backgroundColor: '#FFFFFF',
                                  border: '1px solid #E5E7EB',
                                  borderRadius: '8px',
                                  padding: '14px 12px',
                                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                  zIndex: 10,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  width: '48px'
                                }}>
                                  <input
                                    type="range"
                                    min="0"
                                    max="1"
                                    step="0.05"
                                    value={volume}
                                    onChange={(e) => setVolume(parseFloat(e.target.value))}
                                    orient="vertical"
                                    style={{
                                      WebkitAppearance: 'slider-vertical',
                                      width: '4px',
                                      height: '90px',
                                      cursor: 'pointer',
                                      background: `linear-gradient(to top, #3B82F6 ${volume * 100}%, #E5E7EB ${volume * 100}%)`,
                                      borderRadius: '2px',
                                      outline: 'none'
                                    }}
                                  />
                                  <div style={{
                                    textAlign: 'center',
                                    fontSize: '11px',
                                    color: '#6B7280',
                                    fontWeight: '600',
                                    marginTop: '10px'
                                  }}>
                                    {Math.round(volume * 100)}%
                                  </div>
                                </div>
                              )}
                            </div>

                            <button
                              onClick={toggleFullscreen}
                              style={{
                                backgroundColor: '#FFFFFF',
                                border: '1px solid #E5E7EB',
                                borderRadius: '8px',
                                color: '#374151',
                                cursor: 'pointer',
                                padding: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                              aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                            >
                              <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                                {isFullscreen ? (
                                  <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/>
                                ) : (
                                  <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
                                )}
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Log Practice Section */}
                    {showLogPracticeButton && (
                      <div style={{
                        backgroundColor: '#ECFDF5',
                        border: '1px solid #10B981',
                        borderRadius: '12px',
                        padding: '20px',
                        marginBottom: '24px',
                        textAlign: 'center'
                      }}>
                        <h4 style={{
                          fontSize: isMobile ? '16px' : '18px',
                          fontWeight: '600',
                          color: '#374151',
                          margin: '0 0 12px 0'
                        }}>
                          {selectedPractice.isGoalBased ? '🎯 Ready to log your goal work?' : '🎉 Great job!'}
                        </h4>
                        <p style={{
                          fontSize: isMobile ? '13px' : '14px',
                          color: '#374151',
                          margin: '0 0 16px 0'
                        }}>
                          {selectedPractice.isGoalBased 
                            ? 'Log the time you spent working on your life goals and meaningful activities.'
                            : 'You\'ve completed the practice. Would you like to log this session?'
                          }
                        </p>
                        <div style={{ 
                          display: 'flex', 
                          gap: '12px', 
                          justifyContent: 'center',
                          flexWrap: 'wrap'
                        }}>
                          <button
                            onClick={handleAutoLogPractice}
                            style={{
                              backgroundColor: '#1B365D',
                              color: '#FFFFFF',
                              border: '1px solid #E5E7EB',
                              borderRadius: '24px',
                              padding: isMobile ? '10px 16px' : '12px 20px',
                              fontSize: isMobile ? '13px' : '14px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              fontFamily: 'system-ui, -apple-system, sans-serif'
                            }}
                          >
                            Log Practice
                          </button>
                          <button
                            onClick={() => setShowLogPracticeButton(false)}
                            style={{
                              backgroundColor: 'transparent',
                              color: '#374151',
                              border: '1px solid #E5E7EB',
                              borderRadius: '24px',
                              padding: isMobile ? '10px 16px' : '12px 20px',
                              fontSize: isMobile ? '13px' : '14px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              fontFamily: 'system-ui, -apple-system, sans-serif'
                            }}
                          >
                            Skip
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Action Buttons */}
                    {selectedPractice.isGoalBased && (
                      <div style={{
                        display: 'flex',
                        gap: '12px',
                        justifyContent: 'center',
                        marginTop: isMobile ? '20px' : 'auto',
                        flexWrap: 'wrap'
                      }}>
                        <button
                          onClick={() => setShowLogPracticeButton(true)}
                          style={{
                            backgroundColor: '#EFF6FF',
                            color: '#1B365D',
                            border: '1px solid #3B82F6',
                            borderRadius: '24px',
                            padding: isMobile ? '10px 20px' : '12px 24px',
                            fontSize: isMobile ? '14px' : '16px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            fontFamily: 'system-ui, -apple-system, sans-serif',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}
                        >
                          <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                          </svg>
                          Log Goal Work
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  /* No practice selected */
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 1,
                    textAlign: 'center'
                  }}>
                    <div style={{
                      width: '80px',
                      height: '80px',
                      borderRadius: '50%',
                      backgroundColor: '#F8F9FA',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '24px',
                      border: '1px solid #E5E7EB'
                    }}>
                      <svg width="40" height="40" fill="#6B7280" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </div>
                    <h3 style={{
                      fontSize: isMobile ? '16px' : '20px',
                      fontWeight: '500',
                      color: '#6B7280',
                      margin: '0 0 8px 0',
                      fontFamily: 'system-ui, -apple-system, sans-serif'
                    }}>
                      Select a practice to begin
                    </h3>
                    <p style={{
                      fontSize: isMobile ? '13px' : '14px',
                      color: '#9CA3AF',
                      margin: 0
                    }}>
                      Choose a guided practice from the list to start
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* No practices available */
            <div style={{ 
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              padding: isMobile ? '40px 20px' : '64px 32px',
              textAlign: 'center',
              border: '1px solid #E5E7EB',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{
                width: isMobile ? '60px' : '80px',
                height: isMobile ? '60px' : '80px',
                borderRadius: '50%',
                backgroundColor: '#F8F9FA',
                border: '1px solid #E5E7EB',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px'
              }}>
                <svg width={isMobile ? "30" : "40"} height={isMobile ? "30" : "40"} fill="#6B7280" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              </div>
              <h3 style={{ 
                fontSize: isMobile ? '20px' : '24px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '12px',
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }}>
                Complete sessions to unlock practices
              </h3>
              <p style={{ 
                fontSize: isMobile ? '14px' : '16px',
                color: '#6B7280',
                marginBottom: '32px',
                maxWidth: '500px',
                margin: '0 auto 32px'
              }}>
                Watch session videos to unlock guided practice videos for each technique you learn.
              </p>
              <button
                onClick={() => navigate('/dashboard')}
                style={{
                  backgroundColor: '#1B365D',
                  color: '#FFFFFF',
                  border: '1px solid #E5E7EB',
                  borderRadius: '24px',
                  padding: isMobile ? '10px 20px' : '12px 24px',
                  fontSize: isMobile ? '14px' : '16px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily: 'system-ui, -apple-system, sans-serif'
                }}
              >
                Go to Dashboard
              </button>
            </div>
          )}
        </div>
      </MainLayout>
      
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          video::-webkit-media-controls {
            display: none !important;
          }
          
          video::-webkit-media-controls-enclosure {
            display: none !important;
          }
          
          /* Mobile-specific styles */
          @media (max-width: 768px) {
            * {
              box-sizing: border-box;
            }
            
            body {
              overflow-x: hidden;
              margin: 0;
              padding: 0;
            }
            
            /* Force all containers to stay within viewport */
            .dashboard-container, 
            .dashboard-container * {
              max-width: 100vw !important;
              box-sizing: border-box !important;
            }
            
            /* Prevent any element from extending beyond screen */
            div, section, article, main {
              max-width: 100% !important;
              overflow-x: hidden !important;
            }
          }
        `
      }} />
    </div>
  );
};

export default ModernPracticeLibrary;